// F-CR-merge: modal pra escolher qual parcela do sistema vai receber os
// dados da parcela CA órfã. Carrega candidatos via fin_buscar_candidatos_merge_cr
// e aplica via fin_mesclar_cr (irreversivel — a parcela CA é DELETADA).
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmt, fmtData } from './utils';
import type { Parcela } from './types';

export function ModalMesclarParcelaCr({ parcela, onFechar, onMesclou }: {
  parcela: Parcela;
  onFechar: () => void;
  onMesclou: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [resultado, setResultado] = useState<any>(null);
  const [aplicando, setAplicando] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'fin_buscar_candidatos_merge_cr', id: parcela.id },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setResultado(d);
      } catch (e: any) {
        alert('Erro: ' + (e.response?.data?.error || e.message));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function mesclar(sistemaId: string) {
    if (!window.confirm('Mesclar com esta parcela?\n\nA parcela CA será DELETADA (seus dados — id_externo_ca, baixas, conta financeira, etc. — passam pra parcela do sistema). Operação irreversível.')) return;
    setAplicando(sistemaId);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_mesclar_cr', ca_id: parcela.id, sistema_id: sistemaId },
        { headers: { Authorization: token } });
      onMesclou();
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setAplicando(null); }
  }

  // FIN-UI (2026-07-14): portalizado pro body (fixed dentro das Tabs do hub caía pra baixo).
  return createPortal(
    <div onClick={onFechar} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 8, padding: 24, maxWidth: 800,
        width: '90%', maxHeight: '85vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#020D7F' }}>🔗 Mesclar com parcela existente</h3>
          <button onClick={onFechar}
            style={{ marginLeft: 'auto', background: 'transparent', border: 'none',
              fontSize: 20, color: '#888', cursor: 'pointer' }}>×</button>
        </div>

        {/* Parcela origem (CA) */}
        <div style={{ padding: 12, background: '#FFF8E8', borderRadius: 6,
          border: '0.5px solid #F0D88E', marginBottom: 14, fontSize: 12 }}>
          <div style={{ fontWeight: 600, color: '#7A5500', marginBottom: 6 }}>
            Parcela atual (vinda do Conta Azul):
          </div>
          <div><strong>{parcela.descricao || parcela.parcela_titulo || '(sem descrição)'}</strong></div>
          <div style={{ color: '#666', fontSize: 11 }}>
            {fmt(parcela.valor_previsto)} · venc {fmtData(parcela.data_vencimento)}
            {' · cliente '}
            {(parcela as any).cliente_nome
              || (parcela as any).origem_ref?.cliente_ca?.nome
              || '—'}
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Carregando candidatos…</div>
        ) : !resultado?.candidatos?.length ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#aaa', fontStyle: 'italic' }}>
            Nenhum candidato encontrado (mesmo valor ± 1%, mesmo período ± 30 dias).
            Se essa parcela é única, classifique pelo tipo de receita.
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: '#444', marginBottom: 10 }}>
              {resultado.candidatos.length} candidato{resultado.candidatos.length !== 1 ? 's' : ''} encontrado.
              Quanto maior o score, mais provável o match. Clique em <strong>Mesclar</strong> pra confirmar.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {resultado.candidatos.map((c: any) => {
                const ehHigh = c.score >= 110;
                return (
                  <div key={c.id} style={{
                    padding: 12, borderRadius: 6,
                    border: `1px solid ${ehHigh ? '#0a7f4a' : '#ddd'}`,
                    background: ehHigh ? '#F4FBF6' : '#fff',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3,
                          background: ehHigh ? '#0a7f4a' : '#e0e0e0',
                          color: ehHigh ? '#fff' : '#555', fontWeight: 600 }}>
                          score {c.score} {ehHigh && '(HIGH)'}
                        </span>
                        {c.contrato_numero && (
                          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3,
                            background: '#EFF3FF', color: '#0930D0', fontWeight: 600,
                            fontFamily: 'monospace' }}>
                            {c.contrato_numero}
                          </span>
                        )}
                        <span style={{ fontSize: 10, color: '#888' }}>{c.origem}</span>
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {c.parcela_titulo || c.descricao || '(sem título)'}
                      </div>
                      <div style={{ fontSize: 11, color: '#666' }}>
                        {fmt(c.valor_previsto)} · venc {fmtData(c.data_vencimento)} · cliente {c.cliente_nome || '—'}
                        {c.contrato_titulo && <> · {c.contrato_titulo.slice(0, 60)}</>}
                      </div>
                    </div>
                    <button onClick={() => mesclar(c.id)}
                      disabled={!!aplicando}
                      style={{
                        padding: '8px 16px', fontSize: 12, fontWeight: 600,
                        background: ehHigh ? '#0a7f4a' : '#0930D0', color: '#fff',
                        border: 'none', borderRadius: 4,
                        cursor: aplicando ? 'wait' : 'pointer',
                      }}>
                      {aplicando === c.id ? 'Mesclando…' : '🔗 Mesclar'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
